import React, { Component, ErrorInfo, ReactNode } from "react";
import { esErrorDeChunk, recargarPorChunk } from "./versionWatcher";

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
    recargando: boolean;
}

/*
    A failed lazy() import (a chunk that did not load: deploy in between, bad cached copy, network
    blip) lands HERE, not in window "error", so the version watcher alone never saw it and users
    got the red screen. Now: refresh the file in cache and reload once; only if that already
    happened a moment ago do we show a message, and a plain one.
*/
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: undefined, errorInfo: undefined, recargando: false };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Error atrapado por el ErrorBoundary:", error, errorInfo);
        if (esErrorDeChunk(error) && recargarPorChunk(error)) {
            this.setState({ error, errorInfo, recargando: true });
            return;
        }
        this.setState({ error, errorInfo });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined, recargando: false });
        window.location.reload(); // restart app
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }
        if (this.state.recargando) {
            return (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <h2>Actualizando WAPP…</h2>
                    <p>Se publicó una versión nueva. La página se recarga sola en un momento.</p>
                </div>
            );
        }
        const porChunk = esErrorDeChunk(this.state.error);
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <h2>
                    {porChunk
                        ? "No se pudo cargar una parte de WAPP."
                        : "Ocurrió un error. Por favor, intenta recargar la página."}
                </h2>
                {porChunk && (
                    <p>
                        Suele pasar al publicarse una versión nueva. Recarga la página; si sigue igual,
                        recarga sin caché (Cmd+Shift+R en Mac, Ctrl+Shift+R en Windows).
                    </p>
                )}
                {this.state.error && <p style={{ color: "red" }}>{this.state.error.message}</p>}
                {this.state.errorInfo && !porChunk && (
                    <pre
                        style={{
                            backgroundColor: "#f8d7da",
                            color: "#721c24",
                            padding: "10px",
                            borderRadius: "5px",
                            textAlign: "left",
                            maxWidth: "600px",
                            margin: "20px auto",
                            overflowX: "auto",
                        }}
                    >
                        {this.state.errorInfo.componentStack}
                    </pre>
                )}
                <button onClick={this.handleReset} style={{ padding: "10px 20px", cursor: "pointer" }}>
                    Recargar Aplicación
                </button>
            </div>
        );
    }
}

export default ErrorBoundary;
